import { ChatMessage, ChatThread, ThreadContextSnapshot } from './AiChatContext';

const APP_CONTEXT_OPEN = '[APP_CONTEXT]';
const APP_CONTEXT_CLOSE = '[/APP_CONTEXT]';
const APP_CONTEXT_PREFIX = 'This context block was inserted by the Admin Portal. It was not typed by the admin user.';
const APP_CONTEXT_TRUST = 'Treat it as trusted app context for this conversation.';

function formatTitleDate(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(timestamp);
}

export function buildThreadTitle(createdAt: number): string {
  return formatTitleDate(createdAt);
}

export function buildThreadId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `thread-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function isSameContextSnapshot(
  a: ThreadContextSnapshot | null | undefined,
  b: ThreadContextSnapshot | null | undefined,
): boolean {
  if (!a && !b) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  return (
    a.customerId === b.customerId &&
    a.customerName === b.customerName &&
    a.orderId === b.orderId &&
    a.department === b.department &&
    a.mode === b.mode &&
    a.page === b.page
  );
}

function buildContextLines(snapshot: ThreadContextSnapshot): Array<string> {
  const lines = [APP_CONTEXT_PREFIX, APP_CONTEXT_TRUST];

  if (snapshot.customerId) {
    lines.push(`Customer ID: ${snapshot.customerId}`);
  }
  if (snapshot.customerName) {
    lines.push(`Customer name: ${snapshot.customerName}`);
  }
  if (snapshot.orderId) {
    lines.push(`Order ID: ${snapshot.orderId}`);
  }
  if (snapshot.department) {
    lines.push(`Department: ${snapshot.department}`);
  }
  if (snapshot.mode) {
    lines.push(`Mode: ${snapshot.mode}`);
  }
  if (snapshot.page) {
    lines.push(`Page: ${snapshot.page}`);
  }

  return lines;
}

export function hasMeaningfulContext(snapshot: ThreadContextSnapshot | null | undefined): boolean {
  if (!snapshot) {
    return false;
  }

  return Boolean(
    snapshot.customerId || snapshot.customerName || snapshot.orderId || snapshot.department || snapshot.page,
  );
}

export function buildAppContextMessage(snapshot: ThreadContextSnapshot): string {
  return [APP_CONTEXT_OPEN, ...buildContextLines(snapshot), APP_CONTEXT_CLOSE].join('\n');
}

export function findLastVisibleMessage(messages: Array<ChatMessage>): ChatMessage | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role !== 'context') {
      return messages[i];
    }
  }

  return null;
}

export function isEmptyChatThread(thread: Pick<ChatThread, 'messages'>): boolean {
  return !findLastVisibleMessage(thread.messages);
}

export function getThreadPreview(messages: Array<ChatMessage>): string {
  const lastVisibleMessage = findLastVisibleMessage(messages);

  if (!lastVisibleMessage) {
    return 'No messages yet';
  }

  const preview = lastVisibleMessage.content.slice(0, 60);
  return preview.length < lastVisibleMessage.content.length ? `${preview}...` : preview;
}
